import type { Messages, DeepPartial } from './fr'

// Phase 1 : vide → fallback FR au runtime. Rempli en Phase 2.
export const en: DeepPartial<Messages> = {
  legalLayout: {
    home: 'Home',
    lastUpdated: 'Last updated',
  },
  footer: {
    tagline:
      'Your platform to run your entire e-commerce logistics. Warehouse in the heart of Europe.',
    colProduct: 'Product',
    colCompany: 'Company',
    colResources: 'Resources',
    colLegal: 'Legal',
    linkSectors: 'Industries',
    linkFulfillment: 'DNVB & D2C fulfilment',
    linkPricing: 'Pricing',
    linkStorage: 'Storage',
    linkPickpack: 'Pick & Pack',
    linkShipping: 'Next-day shipping',
    linkReturns: 'Returns',
    linkCarriers: 'Carriers',
    linkAdr: 'ADR storage',
    linkAfsca: 'AFSCA storage',
    linkAbout: 'About',
    linkBlog: 'Blog',
    linkContact: 'Contact',
    linkFaq: 'FAQ',
    linkIntegrations: 'Integrations',
    linkTestimonials: 'Testimonials',
    linkLegalNotice: 'Legal notice',
    linkTerms: 'Terms & conditions',
    linkPrivacy: 'Privacy policy',
    linkCookies: 'Cookie policy',
    socialAria: 'Yaslan on {name}',
    manageCookies: 'Manage cookies',
    systemsOperational: 'All systems operational',
  },
  nav: {
    cta: 'Talk to an expert',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    menuServices: 'Services',
    menuSecteurs: 'Industries',
    menuRessources: 'Resources',
    menuExpertises: 'Expertise',
    colParService: 'By service',
    colOutils: 'Tools',
    colModeBeaute: 'Fashion & Beauty',
    colMaisonOutdoor: 'Home & Outdoor',
    colLecture: 'Reading',
    colSociete: 'Company',
    storageLabel: 'Storage',
    storageDesc: 'Flexible European warehouse',
    pickpackLabel: 'Pick & Pack',
    pickpackDesc: 'Fulfilment in your brand colours',
    shippingLabel: 'Next-day shipping',
    shippingDesc: '3pm cut-off, delivered tomorrow',
    returnsLabel: 'Returns',
    returnsDesc: 'White-label portal',
    fulfillmentLabel: 'DNVB & D2C fulfilment',
    fulfillmentDesc: 'Logistics for D2C brands',
    pricingLink: 'Pricing',
    integrationsLabel: 'Integrations',
    integrationsDesc: 'Shopify, WooCommerce, Amazon',
    carriersLabel: 'Carriers',
    carriersDesc: '30+ partners compared',
    modeLabel: 'Fashion & Textile',
    modeDesc: 'Drops, returns, branding',
    cosmetiqueLabel: 'Cosmetics & Beauty',
    cosmetiqueDesc: 'Batches, CLP compliance, kits',
    parfumerieLabel: 'Perfumery',
    parfumerieDesc: 'ADR, bottles, gift sets',
    maisonLabel: 'Home & Decor',
    maisonDesc: 'Fragility, multi-parcel',
    jardinLabel: 'Garden & Outdoor',
    jardinDesc: 'Volumes, highly seasonal',
    electroniqueLabel: 'Electronics & High-Tech',
    electroniqueDesc: 'Serialisation, RMA, ADR',
    blogLabel: 'Blog',
    blogDesc: 'Case studies and guides',
    faqLabel: 'FAQ',
    faqDesc: 'Frequently asked questions',
    aboutLabel: 'About',
    aboutDesc: 'Our purpose',
    contactLabel: 'Contact',
    contactDesc: 'Talk to an expert',
    colExpertise: 'Permits & capabilities',
    adrLabel: 'ADR — Dangerous goods',
    adrDesc: 'Storage & shipping under ADR permit',
    afscaLabel: 'AFSCA — Food',
    afscaDesc: 'AFSCA-registered ambient storage',
  },
  hero: {
    badgeAutopilot: 'Logistics on autopilot',
    badgeWarehouse: 'Warehouse in the heart of Europe',
    headlineLine1Part1: 'Your',
    headlineLine1Part2: 'e-commerce',
    headlineLine1Part3: 'logistics,',
    headlineLine2Part1: 'smooth, reliable',
    headlineLine2Part2: 'and',
    headlineLine2Part3: 'invisible.',
    subhead:
      'Storage, pick & pack, next-day shipping via 30+ carriers and returns management — no subscription, no friction. Your parcels, your brand, our logistics.',
    ctaPrimary: 'Request a quote',
    ctaSecondary: 'Calculate your rate',
    ctaReassurance: 'Free audit in 2 minutes · No subscription',
    trustDelivery: '1–2 day delivery',
    trustAllLevels: 'For beginners and experts alike',
    trustNoCommitment: 'No commitment',
    trustReturns: 'Returns 100% automated',
  },
  cta: {
    badge: 'Free logistics audit',
    headlinePart1: 'Ready to delegate',
    headlinePart2: 'your logistics',
    subhead:
      'Talk to a Yaslan expert. We analyse your current setup, show you how much you could save, and get you live in 15 minutes.',
    ctaPrimary: 'Book a 30-min call',
    ctaSecondary: 'Discover the services',
    trustResponse: 'Reply within 24 business hours',
    trustPayAsYouGo: 'Pay as you go',
    trustOnboarding: '15-min onboarding',
    trustSupport: 'EN/FR support 7am-10pm',
  },
  closing: {
    badge: 'Our commitment · Invisible logistics',
    headlinePart1: 'Logistics that',
    headlinePart2: 'fades into the background',
    subhead:
      'The best e-commerce logistics is the kind no one notices, except you. Three commitments that guide every choice we make, from the first carton received to the final delivery notification.',
    promiseInvisibleTitle: 'Discreet',
    promiseInvisibleDesc:
      'Your customers never see our name. Packaging, returns portal and emails all stay on your brand, from the box to the confirmation email.',
    promiseFlexibleTitle: 'Flexible',
    promiseFlexibleDesc:
      'No subscription, no long-term commitment, no minimum. You pay for what you actually ship, and your rate grows with your business.',
    promiseHumanTitle: 'Human',
    promiseHumanDesc:
      'A Belgium-based team you reach directly, in English or French, who knows your products and your constraints — not a chatbot, not a ticket.',
  },
  stats: {
    eyebrow: 'Hard numbers',
    headline: 'Logistics that keeps up with your growth.',
    shippingLabel: 'Max shipping',
    shippingSub: 'Orders before 3pm',
    carriersLabel: 'Carriers',
    carriersSub: 'GLS, bpost, DPD, UPS...',
    platformsLabel: 'Platforms',
    platformsSub: 'Shopify, WooCommerce, Odoo',
    savingsLabel: 'Transparency',
    savingsSub: "Shipping + pick & pack, that's all",
  },
  brands: {},
  carriers: {
    badgeLabel: 'Carriers',
    badgeCount: '30+ native integrations',
    headlinePart1: 'One integration,',
    headlinePart2: '30+ carriers',
    headlinePart3: 'zero friction.',
    subhead:
      'Bpost, DHL, GLS, UPS, DPD… We compare the rates and print the labels. You track every parcel from a single dashboard.',
    featured: 'Featured',
    negotiatedRates: 'Negotiated rates',
    moreCarriersLine1: 'Even more',
    moreCarriersLine2: 'carriers',
    zoneWorld: 'World',
    leadBpost: 'Standard and fast delivery (next-day) in Belgium.',
    leadGls:
      'Reliable parcel delivery with strong European B2C and B2B coverage.',
    leadDhl: 'The specialist in international express delivery.',
    leadDpd:
      'Solid European network offering flexible delivery options.',
    leadPostnl:
      'The main operator for your shipments to the Netherlands and the Benelux.',
    leadUps: 'Fast global transport with highly accurate tracking.',
    leadFedex: 'Global express delivery for your urgent shipments.',
    leadMondialrelay:
      'Cost-effective solution specialising in pickup-point delivery.',
    leadColisprive: 'Home delivery optimised for e-commerce.',
    leadDeutschepost:
      'Classic and cost-effective postal service for Germany and abroad.',
    leadDsv:
      'Global logistics solutions and freight transport (road, sea, air).',
  },
  services: {
    badgeLabel: 'Services',
    badgeTagline: 'End-to-end logistics',
    headlinePart1: 'One platform that runs',
    headlinePart2: 'your entire logistics',
    subhead:
      'From receiving the product to final delivery — and beyond with returns — Yaslan covers every step, on autopilot.',
    learnMore: 'Learn more',
    storageTitle: 'Storage',
    storageDesc:
      'Centralised e-commerce storage in the heart of Europe, scalable with no minimum volume.',
    storageBullet1: 'Receiving & inspection',
    storageBullet2: 'Real-time stock',
    storageBullet3: 'Racks, pallets & picking bins',
    pickpackTitle: 'Pick & Pack',
    pickpackDesc:
      'Automated fulfilment with packaging 100% customisable to your brand.',
    pickpackBullet1: 'Tailored branding',
    pickpackBullet2: 'Promo inserts',
    pickpackBullet3: 'Fulfilment in under 4 h',
    shippingTitle: 'Next-day shipping',
    shippingDesc:
      '30+ carriers compared automatically. Orders before 3pm shipped the same day.',
    shippingBullet1: '3pm cut-off',
    shippingBullet2: 'Smart routing',
    shippingBullet3: 'Unified tracking',
    returnsTitle: 'Managed returns',
    returnsDesc:
      'Full handling according to your rules. Restocking, refund or resale.',
    returnsBullet1: 'White-label customer portal',
    returnsBullet2: 'Customisable rules',
    returnsBullet3: 'Restocked within 24 h',
  },
  features: {
    badgeLabel: 'Why Yaslan',
    badgeTagline: 'Invisible logistics',
    headlinePart1: 'Zero friction.',
    headlinePart2: 'Zero oversight.',
    subhead:
      'Logistics designed to disappear: you sell, we deliver — your customers only ever see your brand.',
    carriersName: '30+ carriers',
    carriersDesc:
      'GLS, bpost, DPD, UPS, DHL, PostNL… compared and selected automatically, order by order.',
    carriersHighlight: 'Multi-carrier',
    carriersCta: 'View the integrations',
    tariffName: 'Rates aligned with your volume',
    tariffDesc:
      'Whether you ship 100 or 5,000 parcels a month, your rates adjust naturally to your activity.',
    tariffHighlight: 'Dynamic pricing',
    packagingName: 'Packaging in your brand',
    packagingDesc: 'Boxes, mailers, inserts, leaflets — 100% customisable.',
    packagingHighlight: 'Brand-first',
    savingsName: 'One all-in price',
    savingsDesc:
      'Shipping + pick & pack in a single per-order price — decreasing with your volume.',
    savingsHighlight: 'Transparency',
    savingsStrike1: 'Goods receipt & put-away',
    savingsStrike2: 'Storage',
    savingsStrike3: 'WMS subscription',
    savingsTotalLabel: 'Shipping + pick & pack',
    savingsTotalBadge: '1 price',
    supportName: 'EN/FR support 7am-10pm',
    supportDesc: 'A named contact, in English or French. Free logistics audit.',
    supportHighlight: 'Human',
    supportBadge: '2h27',
    supportBadgeLabel: 'average reply',
  },
  integrations: {
    badgeLabel: 'Integrations',
    badgeTagline: 'Real-time central hub',
    headlinePart1: 'Yaslan integrates',
    headlinePart2: 'with your stack',
    headlinePart3: 'and orchestrates',
    headlinePart4: 'your deliveries',
    subhead:
      '16 e-commerce platforms sync in real time. 30+ carriers activate automatically. One hub, zero config.',
    hubLabel: 'Yaslan Hub',
    benefitNoCodeTitle: 'No code',
    benefitNoCodeDesc:
      'Connect your stack in 3 clicks from the dashboard. No dev required.',
    benefitFailoverTitle: 'Carrier failover',
    benefitFailoverDesc:
      'If a carrier goes down, it automatically switches to the next one in under 1 second.',
    benefitWebhooksTitle: 'Real-time webhooks',
    benefitWebhooksDesc:
      'Every event (order, scan, delivery) triggers your automations within milliseconds.',
  },
  faq: {
    eyebrow: 'Frequently asked questions',
    heading: 'Everything you need to know.',
    q1: 'What is Yaslan Logistics?',
    a1: 'Yaslan is a 3PL logistics provider based in Belgium, specialising in e-commerce. We store your products, pick, pack and ship your orders, and handle returns — so you can focus on selling.',
    q2: 'Who is Yaslan for?',
    a2: 'For e-commerce stores of every size: from your first sales to brands scaling to several thousand orders a month. No minimum volume required.',
    q3: 'How does onboarding work?',
    a3: 'In 15 minutes: we connect your store (Shopify, WooCommerce, etc.), receive your stock, and your first orders ship within 24h. Onboarding guided by a dedicated expert.',
    q4: 'What are the shipping lead times?',
    a4: 'Every order placed before 3 pm is picked, packed and shipped the same day. You choose the default carrier or let us optimise automatically.',
    q5: 'How does returns management work?',
    a5: 'You set the rules (restocking, refund, disposal, resale). On receipt, we process each return according to your criteria and update your stock.',
    q6: 'Can my packaging be customised?',
    a6: 'Yes, 100%. Boxes, mailers, tissue paper, inserts, stickers, leaflets — everything can carry your brand. You send us your materials and we add them to fulfilment.',
    q7: 'What is the pricing?',
    a7: "Pay as you go, with no monthly subscription. You pay for two things only: shipping and pick & pack on each order dispatched, as a single all-in price from €6.25 excl. VAT. Goods receipt, put-away, storage, dashboard and integrations are included at no charge. The price moves with three variables only: parcel weight, your monthly volume and the size of your catalogue.",
    q8: 'Which e-commerce platforms do you integrate with?',
    a8: 'Shopify, WooCommerce, Amazon, PrestaShop, bol, BigCommerce, eBay, Etsy, Lightspeed, Magento, Odoo, Squarespace, Temu, TikTok Shop, Wix and Decathlon. Orders, stock and shipping statuses sync in real time — with no development on your side.',
    q9: 'Which carriers are available through Yaslan?',
    a9: 'Over 30 integrated carriers: bpost, DHL, UPS, FedEx, GLS, DPD, Mondial Relay, PostNL, Colis Privé, DSV, Deutsche Post — among others. Our automatic routing picks the best balance of price, lead time and zone according to your rules.',
    q10: 'Is Yaslan suited to international brands?',
    a10: 'Yes — our Belgian warehouse serves the entire European Union in 24-72h. We also ship to the United Kingdom and Switzerland with customs documentation included. We do not serve destinations outside Europe (United States, Asia, etc.) — only continental Europe, the United Kingdom and Switzerland are covered.',
    q11: 'How does Yaslan protect my data and my stock?',
    a11: 'Warehouse under 24/7 video surveillance with biometric access, tamper-proof seals on outgoing boxes, encrypted backups of customer data, GDPR compliance. Automated weekly inventories and full access to your dashboard.',
    q12: 'Can I switch providers if Yaslan is not right for me?',
    a12: 'No fixed-term commitment. You recover your stock and your data whenever you like. We even make migration to another 3PL easy — history, product data, carrier records — so you stay in control.',
  },
  appMockup: {
    statusInTransit: 'In transit',
    statusDelivered: 'Delivered',
    statusPreparing: 'Preparing',
    splashTagline: 'Logistics',
    loadingTitle: 'Syncing carriers',
    loadingCarriers: 'GLS · bpost · DPD · UPS · DHL · PostNL...',
    notifDeliveredTitle: 'Parcel delivered',
    notifDeliveredSub: 'YSL-8411 — Brussels',
    notifOrdersTitle: '+12 orders',
    notifOrdersSub: 'synced from Shopify',
    navDashboard: 'Dashboard',
    navShipments: 'Shipments',
    navReturns: 'Returns',
    navStock: 'Stock',
    navCarriers: 'Carriers',
    navIntegrations: 'Integrations',
    sidebarWarehouse: 'Warehouse · Belgium',
    sidebarOperational: 'Operational',
    topbarDate: 'Wednesday 23 April',
    topbarTitle: 'Overview',
    topbarSearch: 'Search',
    topbarLive: 'Live',
    kpiShipments24h: '24h shipments',
    kpiInTransit: 'In transit',
    kpiDelivered: 'Delivered',
    kpiReturns: 'Returns',
    shipmentsTitle: 'Shipments in progress',
    shipmentsRealtime: 'Real time',
    routesTitle: 'Active routes',
    routesRegion: 'Europe',
    carriersTitle: 'Carriers',
    carriersWindow: '24h',
    hubLabel: 'BE · Hub',
  },
  about: {
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'About',
    heroBadge: 'About · Our reason for being',
    heroTitlePart1: 'Freeing entrepreneurs from',
    heroTitleHighlight: 'logistics',
    heroSubtitle:
      'Building the logistics operator that ambitious brands deserved: fast, flexible, transparent — and invisible to the end customer.',
    number1Value: '30+',
    number1Label: 'Brands supported',
    number2Value: '€0',
    number2Label: 'Hidden fees',
    number3Value: '< 4h',
    number3Label: 'Fulfilment cut-off',
    number4Value: '99.7%',
    number4Label: 'On-time shipping',
    convictionLabel: 'Our conviction',
    convictionQuotePart1: 'Logistics should never be a',
    convictionQuoteHighlight1: 'brake on growth',
    convictionQuotePart2: 'but, on the contrary, an',
    convictionQuoteHighlight2: 'engine',
    convictionAuthor: '— The founders of Yaslan',
    storyBadge: 'Our origin',
    storyTitlePart1: 'The idea that',
    storyTitleHighlight: 'changed everything',
    storyParagraph1:
      'For years, we watched talented brands slowed down by the same obstacles: locked-in contracts, hidden fees, silent carriers, support nowhere to be found after 6pm. Logistics built for itself, not for the people who had to put up with it.',
    storyQuotePart1: 'What if we built the operator',
    storyQuoteHighlight: 'we ourselves',
    storyQuotePart2: 'would have wanted to find?',
    storyParagraph2Part1:
      'Yaslan is that answer. A service that steps aside for the brand, that scales with volume without constraints, that replies in plain language — on time, no jargon. Not just another platform.',
    storyParagraph2Highlight: 'The operator we would have wanted to have.',
    methodBadge: 'Our method · 4 steps',
    methodTitlePart1: 'From connection to',
    methodTitleHighlight: 'optimisation',
    step1Title: 'Connection',
    step1Desc:
      'Automatic synchronisation with Shopify, WooCommerce, Prestashop and Amazon in minutes.',
    step2Title: 'Receiving',
    step2Desc:
      'Quality control, counting, putaway. Your products are ready within 24 hours.',
    step3Title: 'Shipping',
    step3Desc: 'Picking, packing, labelling. Cut-off at 3pm, delivery from the next day.',
    step4Title: 'Optimisation',
    step4Desc:
      'Tracking volumes and costs, continuous adjustments to bring your bill down.',
    valuesBadge: 'What guides us',
    valuesTitlePart1: 'Three principles,',
    valuesTitleHighlight: 'zero compromise',
    value1Title: 'Human',
    value1Desc:
      'A reachable team that knows your brand and your products.',
    value2Title: 'Flexible',
    value2Desc:
      'No minimum, no long-term commitment. From your first parcel to the thousands that follow.',
    value3Title: 'Transparent',
    value3Desc:
      'Clear pricing per m³ and per parcel, a real-time dashboard, no hidden lines.',
    ctaBadge: 'Onboarding 2026 · limited spots',
    ctaTitlePart1: 'What if your logistics',
    ctaTitleHighlight: 'disappeared',
    ctaTitlePart2: 'from your daily life?',
    ctaSubtitle:
      '30 minutes to understand your flow. A costed pricing proposal within 48h. Go live in under a week.',
    ctaPrimary: 'Book a 30-min call',
    ctaSecondary: 'Discover our services',
    ctaTrust1: 'Reply within 24 working hours',
    ctaTrust2: 'No commitment',
    ctaTrust3: '30+ brands trust us',
  },
  contactPage: {
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Contact',
    badge: "Contact · Let's talk logistics",
    titlePart1: "Let's build",
    titleHighlight: 'your logistics',
    subtitlePart1: 'Tell us what you need. We get back to you in',
    subtitleHighlight: 'under 4 working hours',
    subtitlePart2: ', with an initial review of your flow and a ballpark price.',
    responseTitle: 'Reply within 4 working hours',
    responseDesc:
      'Monday to Friday, 7am to 10pm. Our team replies in 2h27 on average.',
    emailLabel: 'Email',
    addressLabel: 'Address',
    addressLine: 'Schoondonkweg 18 bus 16, 2830 Willebroek',
    addressCountry: 'Belgium',
    followLabel: 'Follow us',
    followDesc: 'Behind the scenes, lessons learned',
    fieldNameLabel: 'Full name',
    fieldNamePlaceholder: 'e.g. Camille Dubois',
    fieldEmailLabel: 'Work email',
    fieldEmailPlaceholder: 'you@brand.com',
    fieldPhoneLabel: 'Phone number',
    fieldPhonePlaceholder: '+32 xxx xx xx xx',
    fieldVolumeLabel: 'Estimated monthly volume',
    fieldVolumePlaceholder: 'e.g. 500 orders',
    fieldStorageLabel: 'Estimated storage need',
    fieldStoragePlaceholder: 'e.g. 30 m³',
    serviceLabel: 'Type of service',
    selectPlaceholder: 'Select your option',
    option1: 'Storage only',
    option2: 'Storage + order fulfilment',
    option3: 'Storage + order fulfilment + shipping',
    option4: 'Migration from another 3PL',
    option5: 'Other',
    messageLabel: 'Tell us about your project',
    messagePlaceholder: 'Products, markets, current carriers, specific constraints…',
    privacyPart1: 'By submitting this form you agree to our',
    privacyLink: 'privacy policy',
    privacyPart2: '. No spam, no reselling, ever.',
    submitting: 'Sending…',
    submit: 'Send my request',
    errorGeneric: 'Something went wrong. Please try again.',
    errorNetwork: 'Could not send the form. Check your connection and try again.',
    trust1: 'Reply within 4 working hours',
    trust2: 'No commitment',
    trust3: 'Free audit',
    successTitle: 'Request received.',
    successDescPart1: 'A member of our team will get back to you within',
    successDescHighlight: '4 working hours',
    successDescPart2: 'with an initial review of your flow.',
    successReset: '← Send another request',
    tabCall: 'Book a call',
    tabCallHint: '30 min · video call',
    tabQuote: 'Request a quote',
    tabQuoteHint: '2 min · costed estimate',
    callTitle: 'Pick your slot',
    callDesc:
      'Thirty minutes on video with a logistics expert. We review your current flow and tell you straight whether Yaslan is the right fit.',
    callLoading: 'Loading the calendar…',
    callFallback:
      'Calendar not showing? Your browser may be blocking the script. Open it in a new tab:',
  },
  blog: {
    badgeName: 'Yaslan blog',
    badgeTopic: 'Logistics & e-commerce',
    heroTitleLine1: 'The blog for online retailers who',
    heroTitleHighlight: 'deliver better',
    heroSubtitle:
      'Advice, case studies and deep dives to optimise your logistics, choose the right carriers and scale your e-commerce.',
    categories: {
      all: 'All',
      logistics: 'Logistics',
      ecommerce: 'E-commerce',
      delivery: 'Delivery',
      technology: 'Technology',
      sustainability: 'Sustainability',
    },
    filterAll: 'All',
    countSingular: '{count} article',
    countPlural: '{count} articles',
    byline: 'By {name}',
    readTime: '{n} min read',
    readTimeShort: '{n} min',
    featured: 'Featured',
    readArticle: 'Read the article',
    allArticlesTitle: 'All articles',
    allArticlesCaption: 'Published every week',
    emptyState: 'No articles in this category yet.',
    newsletterBadge: 'Weekly newsletter',
    newsletterTitleLine1: 'Get our best articles',
    newsletterTitleHighlight: 'straight to your inbox',
    newsletterCopy:
      'One email a week. Case studies, carrier benchmarks and best practices to scale your e-commerce. Zero spam.',
    newsletterEmailLabel: 'Email address',
    newsletterPlaceholder: 'you@company.com',
    newsletterSubmit: 'Subscribe',
    newsletterSubmitted: 'Subscribed!',
    newsletterPrivacy:
      'By subscribing, you agree to our privacy policy. Unsubscribe in one click.',
    newsletterSocialProofPart1: 'Joined by',
    newsletterSocialProofCount: '1,200+',
    newsletterSocialProofPart2: 'online retailers',
    article: {
      notFoundTitle: 'Article not found',
      notFoundLink: '← Back to the blog',
      breadcrumbHome: 'Home',
      breadcrumbBlog: 'Blog',
      contentUnavailable:
        'The content of this article is not available yet. Check back soon!',
      relatedHeading: 'Read next',
      ctaBadge: 'Logistics audit · 30 min · free',
      ctaTitleLead: 'What if your logistics',
      ctaTitleHighlight: 'disappeared',
      ctaTitleTail: 'from your daily routine?',
      ctaCopy:
        'Thirty minutes to understand your flow. A costed pricing proposal within 48h. Go live in under a week.',
      ctaPrimary: 'Book a 30-min call',
      ctaSecondary: '← All articles',
      trust1: 'Reply within 24 working hours',
      trust2: 'No commitment',
    },
  },
  sectorsPage: {
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Sectors',
    badge: 'Sectors · Tailored logistics',
    h1Lead: 'Logistics designed for',
    h1Highlight: 'your sector',
    intro:
      'Fashion, cosmetics, electronics, garden & outdoor, home & decor, perfume — every sector imposes its own constraints: compliance, fragility, seasonality, traceability, unboxing. Yaslan adapts its procedures, its packaging and its carrier network to your market to give you a measurable operational advantage.',
    number1Value: '6',
    number1Label: 'Sectors mastered',
    number2Value: '30+',
    number2Label: 'Brands supported',
    number3Value: '€0',
    number3Label: 'Hidden fees',
    gridTitleLead: 'Six sectors,',
    gridTitleHighlight: 'one dedicated expertise',
    gridCaption:
      'Click on your sector to discover our approach, our guarantees and the concrete answers we bring to your operational constraints.',
    cardCta: 'Discover',
    whyBadge: 'Why a specialised 3PL?',
    whyTitleLead: 'A generic 3PL',
    whyTitleHighlight: 'slows you down',
    whyPara1:
      'Outsourcing your e-commerce logistics to a 3PL operator only makes sense if that operator knows your market. A provider that handles textiles and electronics in the same way applies average procedures that produce average results — poorly managed return rates, rising breakage, questionable compliance.',
    whyPara2:
      'Yaslan is a Belgian e-commerce 3PL that has structured its procedures by sector: an ADR channel for perfume, batch traceability for cosmetics, IMEI serialisation for electronics, elastic capacity for fashion drops, heavy handling for garden, shock-proof cushioning for decor. These are distinct skills, not a single process repainted.',
    whyPara3:
      'The result: a controlled logistics cost with zero hidden fees, breakage rates divided by five on sensitive products, and a customer experience that respects your brand’s tone — from box to carrier.',
    ctaBadge: 'Logistics audit · 30 min · free',
    ctaTitleLead: 'What if your logistics',
    ctaTitleHighlight: 'disappeared',
    ctaTitleTail: ' from your day-to-day?',
    ctaCopy:
      '30 minutes to understand your flow. A costed pricing proposal within 48h. Go-live in less than a week.',
    ctaPrimary: 'Book a 30-min call',
    ctaSecondary: 'Learn more about Yaslan',
    trust1: 'Reply within 24 working hours',
    trust2: 'No commitment',
    trust3: '200+ brands trust us',
  },
  sectorPage: {
    notFoundTitle: 'Sector not found',
    notFoundLink: '← View all sectors',
    breadcrumbHome: 'Home',
    breadcrumbSectors: 'Sectors',
    badgeSuffix: ' · specialised 3PL',
    challengesHeadingLead: 'The logistics challenges of',
    challengesConstat: 'The reality',
    challengesResponse: 'Our response',
    flowHeadingLead: 'The Yaslan flow for',
    flowIntro:
      'Three stations orchestrated by our team and our systems — every item passes through the checks specific to your sector, with no wasted time.',
    promiseKicker: 'Our commitment',
    statsBadge: 'Measured commitments',
    statsHeadingLead: 'Why choose Yaslan for',
    seasonalHeadingLead: 'Your seasonal peaks,',
    seasonalHeadingHighlight: 'absorbed without disruption',
    seasonalIntro:
      'Elastic capacity and operational reinforcements planned in advance — your high season never becomes your breaking point.',
    faqHeadingLead: 'Frequently asked questions —',
    faqHeadingHighlight: '{sector} logistics',
    otherSectors: 'Other sectors',
    ctaBadge: '{sector} audit · 30 min · free',
    ctaTitleLead: 'What if your logistics',
    ctaTitleHighlight: 'disappeared',
    ctaTitleTail: ' from your day-to-day?',
    ctaCopy:
      '30 minutes to understand your {sector} catalogue and your seasonality. A costed pricing proposal within 48h. Go-live in less than a week.',
    ctaPrimary: 'Book a 30-min call',
    ctaSecondary: '← All sectors',
    trust1: 'Reply within 24 working hours',
    trust2: 'No commitment',
    trust3: '200+ brands trust us',
  },
  servicePage: {
    seoTitle: '{service} e-commerce in Belgium | Yaslan 3PL',
    notFoundTitle: 'Service not found',
    backToHome: '← Back to home',
    breadcrumbHome: 'Home',
    flowHeadingLead: 'How we run your',
    flowIntro:
      'Three stations orchestrated by our team and our systems — every step passes through the checks specific to the service, with no wasted time.',
    statsBadge: 'Measured commitments',
    statsHeadingLead: 'Three numbers that',
    statsHeadingHighlight: 'change everything',
    promiseKicker: 'Our promise',
    featuresHeadingLead: 'Everything',
    featuresHeadingHighlight: 'included',
    faqHeadingLead: 'Frequently asked questions —',
    otherServicesHeading: 'Also:',
    otherServicesLink: 'Discover',
    ctaBadge: '{service} audit · 30 min · free',
    ctaTitleLead: 'What if your logistics',
    ctaTitleHighlight: 'disappeared',
    ctaTitleTail: ' from your day-to-day?',
    ctaCopy:
      '30 minutes to understand your flow. A costed pricing proposal within 48h. Go-live in less than a week.',
    ctaPrimary: 'Book a 30-min call',
    trust1: 'Reply within 24 working hours',
    trust2: 'No commitment',
    trust3: '30+ brands trust us',
  },
  cookieBanner: {
    dialogAriaLabel: 'Cookie consent banner',
    eyebrow: 'Cookies',
    bannerTitle: 'Your privacy, your choices',
    bannerBody:
      'We only use cookies that are strictly necessary for the site to work. With your consent, we also measure traffic anonymously to improve our content.',
    learnMore: 'Learn more',
    customize: 'Customise',
    reject: 'Reject',
    accept: 'Accept',
    closeAria: 'Close',
    prefsEyebrow: 'Cookie preferences',
    prefsTitle: 'Manage my cookies',
    prefsIntro:
      'Choose which categories of cookies you allow. Your preferences are kept for 6 months and can be changed at any time.',
    necessaryTitle: 'Strictly necessary',
    necessaryDesc:
      'Essential for the site to work (remembering your consent choices). Always on.',
    necessaryBadge: 'On',
    analyticsTitle: 'Traffic measurement',
    analyticsDesc:
      'Helps us understand how our site is used, anonymously. No advertising profiling.',
    rejectAll: 'Reject all',
    save: 'Save',
    acceptAll: 'Accept all',
    prefsFooterPart1: 'Learn more in our',
    prefsFooterLink: 'Cookie policy',
  },
  breadcrumb: {
    adr: 'ADR',
    afsca: 'AFSCA',
    home: 'Home',
    about: 'About',
    sectors: 'Sectors',
    blog: 'Blog',
    contact: 'Contact',
    cookies: 'Cookie policy',
    legal: 'Legal notice',
    privacy: 'Privacy policy',
    terms: 'Terms & conditions',
  },
  adrPage: {
    // ── Hero ──
    badge: 'Dangerous goods · ADR',
    h1Lead: 'Store and ship your',
    h1Highlight: 'dangerous goods',
    h1Tail: ' e-commerce.',
    subhead:
      'Lithium batteries, aerosols, flammables, corrosives, alcohol-based cosmetics… We hold an ADR storage permit and ship your sensitive products anywhere in Europe — where most 3PLs and e-commerce platforms refuse.',
    ctaPrimary: 'Free audit',
    reassure: 'Reply within 4 working hours · No subscription',
    // ── Hook différenciant ──
    hook: {
      eyebrow: 'What sets us apart',
      title: 'What most 3PLs refuse to handle',
      lead: 'Most logistics providers and e-commerce platforms block dangerous goods right at the product page. We store and ship them.',
      points: [
        'A warehouse with an ADR storage permit, not just a shelf.',
        'ADR-approved carriers for your high-volume B2B shipments.',
        'A compliant framework, from storage to final delivery.',
      ],
    },
    // ── Scope OUI / NON ──
    scope: {
      eyebrow: 'Scope',
      title: 'What we handle, what we don’t',
      statement:
        'Dangerous goods don’t mean rejected goods: if it falls within our ADR scope, we store and ship it — if not, we tell you plainly.',
      yesTitle: 'We store & ship',
      yes: [
        'Lithium batteries and cells',
        'Aerosols and pressurised sprays',
        'Flammable liquids',
        'Corrosive products',
        'Alcohol-based cosmetics (perfumes…)',
        'Cleaning products',
      ],
      noTitle: 'Out of scope',
      no: [
        'Explosives (class 1)',
        'Radioactive materials (class 7)',
      ],
      note: 'Unsure about your product? Send us its safety data sheet (SDS): we’ll give you a clear answer, yes or no.',
    },
    // ── Comment ça marche ──
    how: {
      eyebrow: 'How it works',
      title: 'From storage to shipping, two flows under control',
      steps: [
        {
          title: 'Compliant storage',
          desc: 'Your dangerous goods are stored under an ADR permit, separated and labelled according to their class.',
        },
        {
          title: 'B2C shipping',
          desc: 'One product, one destination: we ship via the carriers in our usual network, within the permitted quantities.',
        },
        {
          title: 'High-volume B2B shipping',
          desc: 'Beyond roughly 2 pallets, we go through an ADR-approved carrier: DSV.',
        },
      ],
    },
    // ── Conformité & garanties ──
    compliance: {
      eyebrow: 'Compliance & guarantees',
      title: 'A regulatory framework, not a grey area',
      items: [
        {
          title: 'ADR storage permit',
          desc: 'Our warehouse is authorised to store e-commerce dangerous goods in the classes we handle.',
        },
        {
          title: 'Approved carriers',
          desc: 'For high-volume B2B, we work with DSV, approved for ADR transport.',
        },
        {
          title: 'Packaging & labelling',
          desc: 'Compliant marking, hazard labels and transport documents on every shipment.',
        },
        {
          title: 'Class segregation',
          desc: 'Incompatible products are stored separately to avoid any risk.',
        },
      ],
    },
    // ── FAQ (longue traîne SEO) ──
    faqEyebrow: 'FAQ',
    faqTitle: 'Your questions about ADR storage',
    faq: [
      {
        q: 'What is ADR and who does it affect in e-commerce?',
        a: 'ADR is the European agreement concerning the international carriage of dangerous goods by road. In e-commerce, it covers every product classed as dangerous: lithium batteries, aerosols, perfumes, flammable or corrosive products… As soon as one of these items is stored and then shipped, an ADR framework applies.',
      },
      {
        q: 'Can you store lithium batteries for my online shop?',
        a: 'Yes. Lithium batteries and cells are among the dangerous goods we store under an ADR permit and ship, both B2C and B2B, in line with regulatory quantities and packaging.',
      },
      {
        q: 'Which dangerous products can you not handle?',
        a: 'We do not handle explosives (class 1) or radioactive materials (class 7), which fall outside the e-commerce scope. For everything else — lithium, aerosols, flammables, corrosives, alcohol-based cosmetics — send us the safety data sheet and we’ll confirm we can take it on.',
      },
      {
        q: 'How do you ship dangerous goods B2C?',
        a: 'For a single shipment (one product to one destination), we ship via the carriers in our usual network, within the quantity limits set for dangerous-goods parcels.',
      },
      {
        q: 'And for B2B volumes (several pallets)?',
        a: 'Beyond around two pallets, we go through an ADR-approved carrier — DSV — which has the resources and certifications required to transport dangerous goods in volume.',
      },
      {
        q: 'Do ADR products need specific packaging?',
        a: 'Yes. Every dangerous-goods shipment requires approved packaging, compliant marking and hazard labels, along with the appropriate transport documents. We take care of all of it.',
      },
      {
        q: 'Do you deliver dangerous goods across Europe?',
        a: 'Yes, we ship across Europe, the UK and Switzerland. The exact terms depend on the product class, the quantity and the destination — we confirm each case during the audit.',
      },
    ],
    // ── Libellés d'animations (composants bespoke) ──
    anim: {
      diamond: {
        badge: 'Classes handled',
        classes: ['Lithium', 'Aerosols', 'Flammables', 'Corrosives'],
      },
      sort: {
        badge: 'Compliance sorting',
        accepted: 'Accepted',
        rejected: 'Out of scope',
      },
      split: {
        badge: 'Two flows',
        stored: 'ADR stock',
        b2c: 'B2C',
        b2cDesc: '1 product → 1 customer',
        b2b: 'B2B',
        b2bDesc: 'Volumes · pallets',
        carriers: 'DSV',
      },
    },
  },
  afscaPage: {
    // ── Hero ──
    badge: 'Food products · AFSCA',
    h1Lead: 'Your food storage,',
    h1Highlight: 'AFSCA-registered',
    h1Tail: '.',
    subhead:
      'Supplements, non-perishable drinks, tea, dry groceries… We are registered with AFSCA for ambient-temperature storage, with FEFO rotation, date management and lot traceability.',
    ctaPrimary: 'Free audit',
    reassure: 'Reply within 4 working hours · No subscription',
    // ── Hook différenciant ──
    hook: {
      eyebrow: 'What sets us apart',
      title: 'The food logistics many 3PLs avoid',
      lead: 'Storing food products requires a framework few e-commerce providers take on. We do — for ambient-temperature products.',
      points: [
        'AFSCA registration for ambient-temperature storage.',
        'FEFO rotation and DLC/DDM (use-by/best-before) management, not blind first-in, first-out.',
        'Lot traceability and recall capability.',
      ],
    },
    // ── Scope OUI / NON ──
    scope: {
      eyebrow: 'Scope',
      title: 'What we store, what we don’t',
      yesTitle: 'We store (ambient)',
      yes: [
        'Food supplements',
        'Non-perishable drinks (water…)',
        'Tea and infusions',
        'Dry groceries',
        'Ambient pre-packaged products',
      ],
      noTitle: 'Out of scope',
      no: [
        'Fresh products (0–8 °C)',
        'Frozen goods',
        'Pet food',
      ],
      note: 'We store at ambient temperature only: no cold chain, no frozen goods, no pet food.',
    },
    // ── Gestion du stock ──
    how: {
      eyebrow: 'Stock management',
      title: 'Food-grade management, not just boxing things up',
      steps: [
        {
          title: 'Receiving & checks',
          desc: 'On arrival, we record lots and expiry dates (use-by/best-before, DLC/DDM).',
        },
        {
          title: 'FEFO rotation',
          desc: 'First Expired, First Out: products nearing their date go out first.',
        },
        {
          title: 'Lot traceability',
          desc: 'Every shipment is tied to its lot — essential in the event of a recall.',
        },
      ],
    },
    // ── Conformité & garanties ──
    compliance: {
      eyebrow: 'Compliance & guarantees',
      title: 'The AFSCA framework, in practice',
      items: [
        {
          title: 'AFSCA registration',
          desc: 'We are registered with AFSCA for the storage of food products at ambient temperature.',
        },
        {
          title: 'FEFO & date management',
          desc: 'Rotation driven by use-by/best-before dates (DLC/DDM) to avoid waste and deliver products within their shelf life.',
        },
        {
          title: 'Lot traceability',
          desc: 'Lot tracking from receiving to shipping, so we can react fast in the event of a withdrawal or recall.',
        },
        {
          title: 'Hygiene & segregation',
          desc: 'Clean, dry and organised storage, separated from non-food goods.',
        },
      ],
    },
    // ── FAQ (longue traîne SEO) ──
    faqEyebrow: 'FAQ',
    faqTitle: 'Your questions about food storage',
    faq: [
      {
        q: 'What is AFSCA and why does it matter when selling food online?',
        a: 'AFSCA is the Belgian federal agency for the safety of the food chain. Any business that stores or handles food products must be known to it. Entrusting your stock to an AFSCA-registered provider guarantees that your products are stored within a controlled framework.',
      },
      {
        q: 'Which food products can you store?',
        a: 'We store products at ambient temperature: food supplements, non-perishable drinks, tea and infusions, dry groceries and pre-packaged products. Anything that needs neither refrigeration nor freezing.',
      },
      {
        q: 'Do you store fresh or frozen products?',
        a: 'No. Our registration covers ambient-temperature storage only. We handle neither the cold chain (0–8 °C), nor frozen goods, nor pet food.',
      },
      {
        q: 'How do you manage expiry dates (use-by/best-before, DLC/DDM)?',
        a: 'From receiving, we record the dates and apply FEFO rotation (First Expired, First Out): products nearing their date are shipped first, which reduces waste and ensures products are delivered within their shelf life.',
      },
      {
        q: 'Do you provide lot traceability?',
        a: 'Yes. Every product is tied to its lot, from receiving to shipping. In the event of a withdrawal or recall, we quickly identify the orders concerned.',
      },
      {
        q: 'What happens in the event of a product recall?',
        a: 'Thanks to lot traceability, we can isolate the stock concerned and identify the orders that received the affected lot, so as to carry out a withdrawal or recall as quickly as possible.',
      },
      {
        q: 'What is the difference between a use-by date and a best-before date?',
        a: 'The use-by date (DLC, “use by”) applies to perishable products; the best-before date (DDM, “best before”) applies to stable products such as dry groceries. We manage both in our rotation.',
      },
    ],
    // ── Libellés d'animations (composants bespoke) ──
    anim: {
      shield: {
        badge: 'AFSCA',
        label: 'Compliant',
      },
      gauge: {
        badge: 'Temperature',
        ambient: 'Ambient',
        chilled: 'Chilled',
        frozen: 'Frozen',
      },
      fefo: {
        badge: 'FEFO',
        title: 'First Expired, First Out',
        next: 'Goes out first',
        lot: 'Lot tracked',
      },
    },
  },
  devisWizard: {
    badge: 'Free quote & audit',
    title: 'Estimate your logistics needs',
    subtitle:
      'In 1 minute: get your personalised estimate and book 15 min with a Yaslan expert.',
    progress: { step1: 'Products', step2: 'Volumes', step3: 'Contact details', step4: 'Appointment' },
    nav: { prev: 'Previous', next: 'Next', seeEstimate: 'See my estimate' },
    step1: {
      title: 'Your products',
      sectorLabel: 'Your sector?',
      sourcingLabel: 'Where are your products made / sourced?',
      basketLabel: 'Average order value',
    },
    step2: {
      title: 'Your volumes & delivery',
      ordersLabel: 'Orders per month',
      itemsLabel: 'Average items per parcel',
      weightLabel: 'Average weight per item',
      splitLabel: 'Desired delivery split',
      splitHome: 'Home delivery',
      splitPickup: 'Pickup point',
      zonesLabel: 'Delivery zones',
    },
    step3: {
      title: 'Your contact details',
      nameLabel: 'Full name',
      namePh: 'John Smith',
      emailLabel: 'Work email',
      emailPh: 'you@company.com',
      phoneLabel: 'Phone',
      phonePh: '+44 7XXX XXX XXX',
      websiteLabel: 'Website',
      websitePh: 'your-store.com',
      platformLabel: 'E-commerce platform',
      platformPh: 'Choose…',
      privacy:
        'Your information is used solely to prepare your quote. No reselling, no spam.',
    },
    sectors: {
      mode: 'Fashion & Textile',
      cosmetique: 'Cosmetics & Beauty',
      parfumerie: 'Perfumery',
      maison: 'Home & Decor',
      jardin: 'Garden & Outdoor',
      electronique: 'Electronics',
      food: 'Supplements / Food',
      autre: 'Other',
    },
    sourcing: {
      import: 'Imported (Asia / China)',
      ue: 'EU / Europe',
      local: 'Belgium / local',
      mix: 'Mix',
    },
    zones: {
      be: 'Belgium',
      eu: 'Europe',
      uk: 'United Kingdom',
      ch: 'Switzerland',
      world: 'Worldwide',
    },
    platforms: {
      shopify: 'Shopify',
      woocommerce: 'WooCommerce',
      prestashop: 'PrestaShop',
      magento: 'Magento',
      amazon: 'Amazon',
      other: 'Other / none',
    },
    units: {
      basket: '€',
      orders: 'orders / month',
      items: 'items',
      weight: 'g',
    },
    booking: {
      teaserTitle: 'Your personalised estimate is ready',
      teaserDesc:
        'Book 30 minutes with an expert to receive it in detail and get your bespoke quote.',
      calFallback: 'Trouble loading the calendar? Open it in a new tab.',
      bookedTitle: "You're booked!",
      bookedDesc:
        'We have received your brief — we are preparing your estimate for the appointment. See you very soon!',
    },
    error: {
      required: 'Please fill in the required fields (name, email, phone).',
      briefFailed: 'Your request has been received; book your slot below.',
    },
  },
  seo: {
    adr: {
      title: 'ADR storage — e-commerce dangerous goods — Yaslan',
      description:
        'Yaslan stores and ships your e-commerce dangerous goods (lithium batteries, aerosols, flammables…) with an ADR storage permit. B2C any carrier, B2B via DSV.',
    },
    afsca: {
      title: 'AFSCA storage — ambient e-commerce food logistics — Yaslan',
      description:
        'Yaslan, AFSCA-registered, stores your ambient-temperature food products (supplements, drinks, dry groceries) with FEFO rotation, best-before management and lot traceability.',
    },
    home: {
      title: 'Yaslan — 3PL e-commerce logistics in Belgium',
      description:
        'Yaslan, your Belgian 3PL e-commerce logistics provider. Storage, pick & pack, 24h dispatch and returns management. No subscription.',
    },
    about: {
      title: 'About Yaslan — 3PL e-commerce logistics in Belgium',
      description:
        'Discover Yaslan: a Belgian 3PL logistics provider specialising in e-commerce. Our mission, our 4-step method, and our pledge to make logistics disappear from your day-to-day.',
    },
    contactPage: {
      title: 'Contact Yaslan — 3PL e-commerce logistics in Belgium',
      description:
        'Talk to our logistics team. Reply within 4 working hours and a free audit of your e-commerce flow. Based in Willebroek, delivering across Europe, the UK and Switzerland.',
    },
    blog: {
      title: 'E-commerce logistics blog — Tips & trends — Yaslan',
      description:
        'Practical tips, trend analysis and field insights on e-commerce logistics, 3PL fulfilment and returns management across Europe.',
    },
    sectors: {
      title: 'E-commerce logistics by sector — Yaslan',
      description:
        'Tailored 3PL solutions for your sector: fashion, cosmetics, electronics, garden & outdoor, home & decor, perfume. Storage, fulfilment and 24h dispatch across Europe from Belgium.',
    },
  },
}
